import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  ShieldCheck, Clock, Mail, FileText, ListChecks, AlertTriangle,
  CheckCircle2, ArrowRight, Wallet, Briefcase, BadgeCheck,
  Lock, Receipt, BookOpenCheck, Users, Sparkles, Phone, ChevronDown,
  Menu, X, PlayCircle, Layers, Puzzle, Zap, FileDown, AtSign,
} from "lucide-react";
import RequestCallDialog, { SUPPORT_EMAIL } from "@/components/RequestCallDialog";
import PublicFooter from "@/components/PublicFooter";

// TODO: quando saranno disponibili i dati fiscali definitivi (ragione sociale,
// P.IVA, sede, PEC, REA), inserirli qui e renderli visibili nel footer.

const PRODUCT_NAME = "Incassi.cloud";
const PRODUCT_SUBTITLE = "Gestionale degli incassi giornalieri per agenzie assicurative";
const TRIAL_DAYS = 7;

// 12 funzionalità raggruppate in 3 blocchi (mostrate tutte, no accordion)
const featuresGroups = [
  {
    heading: "Ogni giorno",
    items: [
      { icon: FileText, title: "Foglio cassa giornaliero", desc: "Tutti gli incassi del giorno in un unico spazio chiaro e ordinato. Contante, assegno, bancomat, bonifico e bollettino postale, ciascuno con il proprio totale." },
      { icon: BadgeCheck, title: "Controllo dei versamenti", desc: "Il collaboratore registra l'incasso, tu approvi o rifiuti il versamento. Sai sempre chi ha consegnato e chi no." },
      { icon: Wallet, title: "Da versare", desc: "Un cruscotto unico con quanto ogni collaboratore ha ancora in cassa, quanto è in attesa della tua convalida e quanto è già rientrato." },
      { icon: Clock, title: "Chiusura automatica", desc: "Il foglio si chiude e si archivia agli orari che imposti tu, diversi per ogni giorno della settimana. Puoi impostare chiusure speciali per singole date." },
    ],
  },
  {
    heading: "Il mestiere, non solo i numeri",
    items: [
      { icon: ListChecks, title: "Controllo C/C", desc: "Tutti i bonifici incassati in un elenco unico, da spuntare man mano che li vedi accreditati. Quelli già confermati da un versamento convalidato si segnano da soli." },
      { icon: BookOpenCheck, title: "Conto separato", desc: "Se un collaboratore versa su conto separato, il flusso si adatta: prima l'accredito sul conto, poi la conferma quando il denaro arriva in agenzia. Ogni passaggio resta tracciato." },
      { icon: Receipt, title: "Sospesi e recuperi", desc: "Registra le polizze incassate solo in parte o rimaste sospese. Per ogni sospeso il sistema prevede il recupero corrispondente, così nessun titolo si perde per strada." },
      { icon: Layers, title: "Incasso multiplo e abbuoni", desc: "Un solo pagamento che copre più polizze si registra in una volta. E se applichi uno sconto, l'abbuono ha la sua colonna e rientra nei conti." },
    ],
  },
  {
    heading: "Come lavora la tua agenzia",
    items: [
      { icon: Briefcase, title: "Gestione multi-compagnia", desc: "Colonna Compagnia sui fogli cassa, con riepiloghi unici o separati per ciascuna mandante. Per le agenzie plurimandatarie." },
      { icon: Users, title: "Ogni collaboratore ha le sue regole", desc: "POS che accredita direttamente in agenzia, bonifici sul conto dell'agenzia, chiusura in autonomia, abbuoni, flusso di cassa personale: si attivano collaboratore per collaboratore." },
      { icon: FileDown, title: "Riepiloghi PDF ed Excel", desc: "Ogni chiusura genera i documenti, con il tuo logo e i dati della tua agenzia. Verticali o orizzontali, con i movimenti POS in un file separato se ti serve per la contabilità." },
      { icon: AtSign, title: "Email dal tuo dominio", desc: "I riepiloghi possono partire dalla casella della tua agenzia invece che da un mittente generico. Funziona con Gmail, Outlook, Aruba, Libero e qualsiasi altro provider." },
    ],
  },
];

// Card sezione COMPATIBILITÀ (compare subito dopo la hero)
const compatCards = [
  { icon: Puzzle, title: "Tieni il tuo gestionale", desc: "Portafoglio, polizze e contabilità restano dove sono. Incassi.cloud si occupa solo della cassa e dei versamenti." },
  { icon: Zap, title: "Attivo in pochi minuti", desc: "Nessun trasferimento di archivi, nessun tecnico da chiamare. Crei l'account, inviti i collaboratori e sei operativo." },
  { icon: ShieldCheck, title: "Provi senza rischi", desc: "Sette giorni di prova sulla cassa reale, in parallelo a come lavori oggi. Se non ti serve, non hai cambiato nulla." },
];

const plans = [
  {
    id: "solo",
    name: "Solo",
    price: "19,99",
    priceYear: "227,89",
    tagline: "Per il collaboratore autonomo",
    feats: [
      "1 utenza singola (no collaboratori)",
      "Foglio cassa, versamenti e storico",
      "Storico dei fogli cassa: 6 mesi",
      "Riepiloghi PDF ed Excel via email",
      "Flusso di cassa incluso",
    ],
    highlight: false,
    userLabel: "Solo io",
  },
  {
    id: "base",
    name: "Base",
    price: "29,99",
    priceYear: "341,89",
    tagline: "Per piccole agenzie",
    feats: [
      "1 agente + fino a 3 collaboratori",
      "Fogli cassa illimitati",
      "Storico dei fogli cassa: 6 mesi",
      "Convalida versamenti dei collaboratori",
      "Flusso di cassa agente + collaboratori",
      "Logo e dati agenzia nei riepiloghi",
      "Incasso multiplo e abbuoni",
    ],
    highlight: false,
    userLabel: "Fino a 4 utenti",
  },
  {
    id: "pro",
    name: "Pro",
    price: "39,99",
    priceYear: "455,89",
    tagline: "Per agenzie in crescita",
    feats: [
      "1 agente + fino a 7 collaboratori",
      "Tutto del piano Base",
      "Storico dei fogli cassa: 12 mesi",
      "Gestione multi-compagnia inclusa",
      "Controllo C/C, sospesi e recuperi",
      "Supporto prioritario",
    ],
    highlight: true,
    userLabel: "Fino a 8 utenti",
  },
  {
    id: "unlimited",
    name: "Unlimited",
    price: "49,99",
    priceYear: "569,89",
    tagline: "Per agenzie strutturate",
    feats: [
      "1 agente + fino a 15 collaboratori",
      "Tutto del piano Pro",
      "Storico dei fogli cassa: 5 anni",
      "Gestione multi-compagnia inclusa",
      "Supporto prioritario",
    ],
    highlight: false,
    userLabel: "Fino a 16 utenti",
  },
];

// Blocchi "servizi inclusi" (ex add-on)
const included = [
  { icon: Wallet, name: "Flusso di cassa", desc: "Saldo iniziale, incassi liquidi, uscite e versamenti calcolati in automatico sul foglio cassa.", tag: "Incluso in tutti i piani" },
  { icon: Users, name: "Flusso di cassa per collaboratori", desc: "Attiva il flusso di cassa sui singoli collaboratori senza limiti di attivazioni.", tag: "Incluso in tutti i piani" },
  { icon: Briefcase, name: "Gestione multi-compagnia", desc: "Colonna Compagnia sui fogli cassa e riepiloghi unici o separati per compagnia.", tag: "Inclusa nei piani Pro e Unlimited" },
];

// FAQ (accordion — chiuse inizialmente)
const faqs = [
  ["Devo inserire la carta di credito per iniziare?", `No. La prova di ${TRIAL_DAYS} giorni non richiede pagamento. Inserirai i dati di pagamento solo per attivare l'abbonamento al termine della prova.`],
  ["Incassi.cloud sostituisce il gestionale della compagnia?", "No. Incassi.cloud affianca gli strumenti già utilizzati dall'agenzia e semplifica il controllo interno di incassi, versamenti e collaboratori."],
  ["Posso utilizzarlo da smartphone e tablet?", "Sì. L'interfaccia è responsive: collaboratori e agente possono operare dal computer, dal tablet o dallo smartphone senza differenze funzionali."],
  ["Cosa succede al termine della prova?", `Al termine dei ${TRIAL_DAYS} giorni di prova puoi scegliere se attivare un abbonamento (con carta) oppure sospendere l'account. Nessun addebito automatico prima della tua conferma.`],
  ["Posso cambiare piano o cancellare l'abbonamento?", "Sì, dalla tua area Abbonamento puoi fare upgrade, downgrade o cancellare in qualsiasi momento. L'accesso resta attivo fino alla fine del periodo già pagato."],
  ["Posso esportare i miei dati?", "Sì. Puoi scaricare i riepiloghi giornalieri in PDF ed Excel in qualsiasi momento, oltre a consultare lo storico dei fogli cassa dalla tua area."],
  ["Quanti collaboratori posso aggiungere?", "Dipende dal piano: Solo nessuno, Base fino a 3, Pro fino a 7, Unlimited fino a 15. Per numeri superiori o esigenze particolari possiamo studiare un'offerta personalizzata: scrivici dalla sezione Contatti."],
  ["Posso gestire più compagnie?", "Sì, con la gestione multi-compagnia inclusa nei piani Pro e Unlimited: aggiungi una colonna Compagnia al foglio cassa e scegli se inviare a fine giornata un riepilogo unico o uno separato per ciascuna compagnia."],
  ["Posso utilizzare il mio dominio email?", "Sì. Dall'area Impostazioni puoi configurare i dati SMTP del tuo provider (Gmail, Outlook, Aruba, Libero, server aziendale) per inviare le comunicazioni con il tuo indirizzo."],
  ["Dove vengono conservati i dati?", "Su infrastruttura Aruba, nel data center di Arezzo, in Italia. I dati non vengono trasferiti fuori dal territorio italiano."],
  ["Mi fate firmare un accordo sul trattamento dei dati?", "Sì. All'attivazione è disponibile l'accordo previsto dall'art. 28 del Regolamento (UE) 2016/679, che nomina Incassi.cloud responsabile del trattamento per conto della tua agenzia. Puoi richiederlo a info@incassi.cloud."],
  ["Come funziona la fatturazione?", "L'abbonamento viene fatturato in base al piano scelto (mensile o annuale). Il prezzo finale è sempre indicato prima del pagamento, senza costi nascosti."],
  ["Devo abbandonare il gestionale che uso adesso?", "No. Incassi.cloud non gestisce polizze, portafoglio o contabilità generale: si occupa della cassa e dei versamenti. Continui a usare il tuo gestionale esattamente come prima."],
  ["Come funziona il controllo dei bonifici?", "Trovi tutti i bonifici incassati dall'agenzia e dai collaboratori in un unico elenco, e li spunti man mano che li vedi accreditati sul conto. Quelli già confermati da un versamento convalidato vengono segnati automaticamente."],
  ["Gestisce i collaboratori con conto separato?", "Sì. Per quei collaboratori il versamento avviene in due passaggi: prima l'accredito sul conto separato, poi la conferma quando l'importo arriva in agenzia. Ogni passaggio resta registrato con data e importo."],
  ["Posso registrare un pagamento unico per più polizze?", "Sì, con la funzione incasso multiplo. Un solo bancomat che copre due o tre polizze si registra in una sola operazione, mantenendo il dettaglio dei singoli titoli."],
  ["Posso mettere il logo della mia agenzia sui documenti?", "Sì. Logo, nome, partita IVA, indirizzo e PEC compaiono sull'intestazione dei PDF e nelle email di riepilogo."],
];

function ScrollAnchorLink({ href, className, children, ...rest }) {
  const onClick = (e) => {
    if (href?.startsWith("#")) {
      const el = document.querySelector(href);
      if (el) {
        e.preventDefault();
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };
  return (
    <a href={href} className={className} onClick={onClick} {...rest}>{children}</a>
  );
}

/** Riquadro dimostrativo hero */
function HeroMockCard() {
  return (
    <div className="rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-6 shadow-2xl" aria-hidden="true">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Foglio cassa · Mario Bianchi</div>
        <span className="text-[10px] uppercase tracking-wider bg-amber-500/20 text-amber-200 border border-amber-400/30 px-1.5 py-0.5 rounded font-semibold">Dati dimostrativi</span>
      </div>
      <div className="space-y-2">
        {[
          { id: "d1", c: "Carlo Verdi", p: "12345-A", m: "Contante", k: "Compagnia A", i: "€ 480,00", u: false, s: "Confermato" },
          { id: "d2", c: "ACME Srl", p: "98765-B", m: "Bonifico", k: "Compagnia B", i: "€ 2.150,00", u: true, s: "Urgente" },
          { id: "d3", c: "F. Bianchi", p: "55512-C", m: "Bancomat", k: "Compagnia C", i: "€ 320,50", u: false, s: "Confermato" },
        ].map((r) => (
          <div key={r.id} className={`flex items-center gap-3 p-2.5 rounded-lg ${r.u ? "bg-red-500/15 border border-red-400/30" : "bg-white/5"}`}>
            {r.u && <AlertTriangle className="w-3.5 h-3.5 text-red-300 shrink-0" />}
            <div className="flex-1 text-sm truncate">{r.c}</div>
            <div className="text-[10px] px-2 py-0.5 rounded bg-blue-500/20 text-blue-200 hidden md:block">{r.k}</div>
            <div className="text-xs text-slate-400 hidden md:block">{r.m}</div>
            <div className="text-sm font-mono font-semibold">{r.i}</div>
          </div>
        ))}
      </div>
      <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-3 gap-3 text-center">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Incassato</div>
          <div className="font-heading font-bold text-base text-emerald-300 font-mono">€ 2.950,50</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Versato</div>
          <div className="font-heading font-bold text-base text-blue-300 font-mono">€ 480,00</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Residuo</div>
          <div className="font-heading font-bold text-base text-amber-300 font-mono">€ 2.470,50</div>
        </div>
      </div>
    </div>
  );
}

/** Mockup 1 — Registrazione di un incasso */
function DemoMockRegistrazione() {
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" aria-hidden="true">
      <div className="px-4 py-2 min-h-[48px] bg-slate-900 text-white text-xs flex items-center justify-between gap-2">
        <span className="font-semibold tracking-wide">Nuovo incasso</span>
        <span className="uppercase tracking-wider bg-amber-500/20 text-amber-200 border border-amber-400/40 px-1.5 py-0.5 rounded font-semibold whitespace-nowrap shrink-0">Dati dimostrativi</span>
      </div>
      <div className="p-4 space-y-3 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Contraente</div>
          <div className="mt-1 h-9 rounded-md border border-slate-200 bg-slate-50 px-3 flex items-center text-slate-700">Rossi Mario</div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Numero polizza</div>
            <div className="mt-1 h-9 rounded-md border border-slate-200 bg-slate-50 px-3 flex items-center text-slate-700 font-mono">AB1234567</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Compagnia</div>
            <div className="mt-1 h-9 rounded-md border border-slate-200 bg-slate-50 px-3 flex items-center text-slate-700">Compagnia A</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Metodo</div>
            <div className="mt-1 h-9 rounded-md border border-slate-200 bg-slate-50 px-3 flex items-center text-slate-700">Bancomat</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Importo</div>
            <div className="mt-1 h-9 rounded-md border border-slate-200 bg-slate-50 px-3 flex items-center justify-end text-slate-900 font-mono font-semibold">€ 480,00</div>
          </div>
        </div>
        <button type="button" className="mt-2 w-full h-10 rounded-md bg-slate-900 text-white text-sm font-semibold" tabIndex={-1}>Aggiungi incasso</button>
      </div>
    </div>
  );
}

/** Mockup 2 — Convalida versamento */
function DemoMockConvalida() {
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" aria-hidden="true">
      <div className="px-4 py-2 min-h-[48px] bg-slate-900 text-white text-xs flex items-center justify-between gap-2">
        <span className="font-semibold tracking-wide">Versamento in attesa di convalida</span>
        <span className="uppercase tracking-wider bg-amber-500/20 text-amber-200 border border-amber-400/40 px-1.5 py-0.5 rounded font-semibold whitespace-nowrap shrink-0">Dati dimostrativi</span>
      </div>
      <div className="p-4 space-y-3 text-sm">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-500">Collaboratore</div>
            <div className="font-semibold text-slate-900">Luca Bianchi</div>
          </div>
          <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded font-semibold">In attesa</span>
        </div>
        <div className="rounded-md border border-slate-200 divide-y divide-slate-100">
          <div className="flex justify-between px-3 py-2 text-slate-700">
            <span>Polizza XY-102 · Contante</span><span className="font-mono">€ 180,00</span>
          </div>
          <div className="flex justify-between px-3 py-2 text-slate-700">
            <span>Polizza XY-103 · Assegno</span><span className="font-mono">€ 340,50</span>
          </div>
          <div className="flex justify-between px-3 py-2 bg-slate-50 font-semibold text-slate-900">
            <span>Totale</span><span className="font-mono">€ 520,50</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 pt-1">
          <button type="button" className="h-9 rounded-md border border-slate-300 text-slate-700 text-sm font-semibold" tabIndex={-1}>Rifiuta</button>
          <button type="button" className="h-9 rounded-md bg-emerald-600 text-white text-sm font-semibold" tabIndex={-1}>Convalida</button>
        </div>
      </div>
    </div>
  );
}

/** Mockup 3 — Riepilogo di fine giornata */
function DemoMockRiepilogo() {
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" aria-hidden="true">
      <div className="px-4 py-2 min-h-[48px] bg-slate-900 text-white text-xs flex items-center justify-between gap-2">
        <span className="font-semibold tracking-wide">Riepilogo di fine giornata</span>
        <span className="uppercase tracking-wider bg-amber-500/20 text-amber-200 border border-amber-400/40 px-1.5 py-0.5 rounded font-semibold whitespace-nowrap shrink-0">Dati dimostrativi</span>
      </div>
      <div className="p-4 space-y-3 text-sm">
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-md bg-emerald-50 border border-emerald-100 p-2">
            <div className="text-[10px] uppercase text-emerald-700 font-semibold">Incassato</div>
            <div className="font-mono font-bold text-slate-900">€ 3.150,00</div>
          </div>
          <div className="rounded-md bg-blue-50 border border-blue-100 p-2">
            <div className="text-[10px] uppercase text-blue-700 font-semibold">Versato</div>
            <div className="font-mono font-bold text-slate-900">€ 1.020,50</div>
          </div>
          <div className="rounded-md bg-amber-50 border border-amber-100 p-2">
            <div className="text-[10px] uppercase text-amber-700 font-semibold">Residuo</div>
            <div className="font-mono font-bold text-slate-900">€ 2.129,50</div>
          </div>
        </div>
        <div className="rounded-md border border-slate-200 divide-y divide-slate-100 text-xs">
          <div className="flex justify-between px-3 py-2 bg-slate-50 font-semibold text-slate-700"><span>Metodo</span><span>Totale</span></div>
          <div className="flex justify-between px-3 py-2"><span>Contante</span><span className="font-mono">€ 780,00</span></div>
          <div className="flex justify-between px-3 py-2"><span>Bancomat</span><span className="font-mono">€ 1.220,00</span></div>
          <div className="flex justify-between px-3 py-2"><span>Bonifico</span><span className="font-mono">€ 1.150,00</span></div>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <FileText className="w-3.5 h-3.5" />
          <span>PDF ed Excel inviati per email a fine giornata.</span>
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const [billingCycle, setBillingCycle] = useState("month");
  const [callOpen, setCallOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(-1);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const openDemo = () => setCallOpen(true);
  const closeMobile = () => setMobileNavOpen(false);

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden">
      {/* ===== NAV ===== */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-slate-900 flex items-center justify-center" aria-hidden="true">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-bold text-slate-900" aria-label={`${PRODUCT_NAME} — ${PRODUCT_SUBTITLE}`}>{PRODUCT_NAME}</span>
          </div>
          <nav className="hidden md:flex items-center gap-6 text-sm text-slate-600" aria-label="Navigazione principale">
            <ScrollAnchorLink href="#funzionalita" className="hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded">Funzionalità</ScrollAnchorLink>
            <ScrollAnchorLink href="#come-funziona" className="hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded">Come funziona</ScrollAnchorLink>
            <ScrollAnchorLink href="#prezzi" className="hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded">Prezzi</ScrollAnchorLink>
            <ScrollAnchorLink href="#servizi-inclusi" className="hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded">Servizi inclusi</ScrollAnchorLink>
            <ScrollAnchorLink href="#faq" className="hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded">FAQ</ScrollAnchorLink>
            <ScrollAnchorLink href="#contatti" className="hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded">Contatti</ScrollAnchorLink>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" className="hidden sm:block"><Button variant="outline" size="sm" data-testid="nav-login">Accedi</Button></Link>
            <Link to="/registrazione"><Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="nav-signup">Prova gratis</Button></Link>
            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-100"
              onClick={() => setMobileNavOpen((v) => !v)}
              aria-label={mobileNavOpen ? "Chiudi menu" : "Apri menu"}
              aria-expanded={mobileNavOpen}
              data-testid="nav-mobile-toggle"
            >
              {mobileNavOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {mobileNavOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white" data-testid="nav-mobile-panel">
            <nav className="flex flex-col px-4 py-3 gap-1 text-sm" aria-label="Navigazione mobile">
              <ScrollAnchorLink href="#funzionalita" className="py-2 px-2 rounded hover:bg-slate-100" onClick={closeMobile}>Funzionalità</ScrollAnchorLink>
              <ScrollAnchorLink href="#come-funziona" className="py-2 px-2 rounded hover:bg-slate-100" onClick={closeMobile}>Come funziona</ScrollAnchorLink>
              <ScrollAnchorLink href="#prezzi" className="py-2 px-2 rounded hover:bg-slate-100" onClick={closeMobile}>Prezzi</ScrollAnchorLink>
              <ScrollAnchorLink href="#servizi-inclusi" className="py-2 px-2 rounded hover:bg-slate-100" onClick={closeMobile}>Servizi inclusi</ScrollAnchorLink>
              <ScrollAnchorLink href="#faq" className="py-2 px-2 rounded hover:bg-slate-100" onClick={closeMobile}>FAQ</ScrollAnchorLink>
              <ScrollAnchorLink href="#contatti" className="py-2 px-2 rounded hover:bg-slate-100" onClick={closeMobile}>Contatti</ScrollAnchorLink>
              <Link to="/login" className="py-2 px-2 rounded hover:bg-slate-100" onClick={closeMobile}>Accedi</Link>
            </nav>
          </div>
        )}
      </header>

      {/* ===== HERO ===== */}
      <section className="auth-bg text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24 grid md:grid-cols-2 gap-10 md:gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-xs text-blue-200 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" aria-hidden="true" />
              {TRIAL_DAYS} giorni di prova gratuita · Nessuna carta richiesta
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
              Incassi, versamenti e collaboratori: <span className="text-blue-300">tutto sotto controllo</span>.
            </h1>
            <p className="text-slate-300 text-base md:text-lg mt-6 leading-relaxed">
              Registra gli incassi dei collaboratori, controlla i versamenti, riconcilia i bonifici e ricevi ogni giorno un riepilogo ordinato in PDF ed Excel. Gestisce sospesi, recuperi, abbuoni e conto separato. Pensato per le agenzie assicurative italiane.
            </p>
            <p className="text-slate-400 text-sm mt-3">
              Chiusura automatica, gestione multi-compagnia e storico sempre consultabile.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/registrazione">
                <Button size="lg" className="bg-blue-500 hover:bg-blue-600 text-white" data-testid="hero-cta-primary">
                  Prova gratis <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Button
                size="lg"
                variant="outline"
                type="button"
                onClick={openDemo}
                className="bg-white/5 border-white/20 text-white hover:bg-white/10"
                data-testid="hero-cta-demo"
              >
                Prenota una demo
              </Button>
              <ScrollAnchorLink href="#come-funziona">
                <Button size="lg" variant="ghost" type="button" className="text-blue-200 hover:text-white hover:bg-white/5" data-testid="hero-cta-tertiary">
                  <PlayCircle className="w-4 h-4 mr-2" /> Guarda come funziona
                </Button>
              </ScrollAnchorLink>
            </div>
            <ul className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-10 text-sm text-slate-400">
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" aria-hidden="true" /> Nessuna carta richiesta</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" aria-hidden="true" /> Configurazione guidata</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" aria-hidden="true" /> Assistenza italiana</li>
            </ul>
          </div>
          <div className="relative order-first md:order-last">
            <HeroMockCard />
          </div>
        </div>
      </section>

      {/* ===== COMPATIBILITÀ ===== */}
      <section className="py-16 md:py-20 bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-wider text-blue-600 font-semibold">Compatibilità</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mt-2">Non devi cambiare gestionale.</h2>
            <p className="text-slate-600 mt-4 text-base md:text-lg">
              Incassi.cloud si affianca a quello che usi già. Nessuna migrazione di dati, nessuna riconfigurazione, nessun rischio.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5 mt-10">
            {compatCards.map((it) => (
              <Card key={it.title} className="p-6 border-slate-200 hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center mb-4">
                  <it.icon className="w-5 h-5 text-blue-600" aria-hidden="true" />
                </div>
                <h3 className="font-heading font-semibold text-slate-900">{it.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{it.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PROBLEMA ===== */}
      <section className="py-16 md:py-20 bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900">
              Gestisci ancora incassi e versamenti tra Excel, messaggi e conteggi manuali?
            </h2>
            <p className="text-slate-600 mt-4 text-base md:text-lg">
              Quando i collaboratori aumentano, controllare ogni operazione può diventare lento e poco chiaro.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5 mt-10">
            {[
              { title: "Dati sparsi", desc: "Incassi, versamenti e comunicazioni si trovano spesso in file, email e messaggi differenti." },
              { title: "Controlli manuali", desc: "Per capire cosa è stato versato e cosa manca bisogna confrontare più dati e rifare i conteggi." },
              { title: "Riepiloghi da preparare", desc: "A fine giornata occorre ordinare le operazioni e creare manualmente report e fogli cassa." },
            ].map((it) => (
              <Card key={it.title} className="p-6 border-slate-200">
                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center mb-4">
                  <AlertTriangle className="w-5 h-5 text-amber-600" aria-hidden="true" />
                </div>
                <h3 className="font-heading font-semibold text-slate-900">{it.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{it.desc}</p>
              </Card>
            ))}
          </div>
          <div className="mt-10 flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
            <p className="text-slate-700 md:text-lg flex-1">
              <strong>{PRODUCT_NAME}</strong> riunisce tutto in un unico foglio cassa digitale, aggiornato in tempo reale.
            </p>
            <Link to="/registrazione">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="problem-cta">
                Prova gratis <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== FEATURES ===== */}
      <section id="funzionalita" className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-wider text-blue-600 font-semibold">Funzionalità</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mt-2">Tutto quello che serve per gestire la cassa.</h2>
            <p className="text-slate-600 mt-4">Strumenti pensati per agenzie assicurative italiane, mono-mandatarie e plurimandatarie.</p>
          </div>
          {featuresGroups.map((grp, gi) => (
            <div key={grp.heading} className={gi === 0 ? "mt-12" : "mt-14"}>
              <h3 className="font-heading text-xl md:text-2xl font-semibold text-slate-900">{grp.heading}</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-5">
                {grp.items.map((f) => (
                  <Card key={f.title} className="p-6 border-slate-200 hover:shadow-md transition-shadow">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center mb-4">
                      <f.icon className="w-5 h-5 text-blue-600" aria-hidden="true" />
                    </div>
                    <h4 className="font-heading font-semibold text-slate-900">{f.title}</h4>
                    <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.desc}</p>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== COME FUNZIONA ===== */}
      <section id="come-funziona" className="bg-white py-20 md:py-24 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-wider text-blue-600 font-semibold">Come funziona</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mt-2">Operativo in pochi minuti.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {[
              { n: "1", t: "Configura la tua agenzia", d: "Crea l'account e inserisci le informazioni principali della tua attività." },
              { n: "2", t: "Invita i collaboratori", d: "Ogni collaboratore riceve il proprio accesso e può iniziare a registrare gli incassi." },
              { n: "3", t: "Controlla e ricevi i riepiloghi", d: "Convalida i versamenti e ricevi automaticamente PDF ed Excel a fine giornata." },
            ].map((s) => (
              <div key={s.n} className="relative">
                <div className="font-heading text-6xl font-bold text-slate-100 leading-none" aria-hidden="true">{s.n}</div>
                <h3 className="font-heading font-semibold text-slate-900 mt-2">{s.t}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
          <p className="text-slate-600 mt-10 md:text-lg">
            <ShieldCheck className="w-4 h-4 inline mr-1 text-emerald-600 align-[-2px]" aria-hidden="true" />
            Ti aiutiamo gratuitamente nella configurazione iniziale.
          </p>
        </div>
      </section>

      {/* ===== IN AZIONE ===== */}
      <section id="in-azione" className="py-20 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-wider text-blue-600 font-semibold">Anteprima</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mt-2">Guarda {PRODUCT_NAME} in azione</h2>
            <p className="text-slate-600 mt-4">
              Dalla registrazione dell&apos;incasso al riepilogo finale: tutto il flusso in pochi passaggi.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5 mt-10">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">1 · Registrazione incasso</div>
              <DemoMockRegistrazione />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">2 · Convalida versamento</div>
              <DemoMockConvalida />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">3 · Riepilogo finale</div>
              <DemoMockRiepilogo />
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-3 mt-10">
            <Link to="/registrazione">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="inazione-cta-primary">
                Prova gratis <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Button size="lg" variant="outline" type="button" onClick={openDemo} data-testid="inazione-cta-demo">
              Prenota una demo
            </Button>
          </div>
        </div>
      </section>

      {/* ===== PENSATO DA CHI INCASSA ===== */}
      <section id="pensato-da-chi-incassa" className="bg-white py-20 md:py-24 border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-wider text-blue-600 font-semibold">Pensato da chi incassa</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mt-2">Le cose che solo chi lavora in agenzia sa.</h2>
            <p className="text-slate-600 mt-4 text-base md:text-lg">
              Non è un gestionale generico adattato alle assicurazioni. Sono dettagli che nascono da anni dietro il bancone.
            </p>
          </div>
          <ul className="mt-10 space-y-6">
            {[
              { strong: "Il bonifico non si versa.", rest: "Bonifici, POS e bollettini entrano nel totale del giorno ma restano fuori dalla liquidità da consegnare. Il conteggio di cosa deve rientrare fisicamente è sempre corretto." },
              { strong: "Il POS del collaboratore può essere il tuo.", rest: "Se il suo bancomat accredita direttamente sul conto dell'agenzia, quegli importi vengono esclusi dal versamento in automatico." },
              { strong: "Gli incassi urgenti si vedono subito.", rest: "Segni una riga come urgente e parte una notifica dedicata, senza aspettare la chiusura." },
              { strong: "La giornata finisce quando finisce davvero.", rest: "Orari diversi per ogni giorno, chiusure speciali per le giornate particolari, e i collaboratori che vogliono chiudere da soli possono farlo." },
            ].map((it) => (
              <li key={it.strong} className="flex gap-3">
                <div className="mt-1 shrink-0 w-1.5 h-1.5 rounded-full bg-blue-600" aria-hidden="true" />
                <p className="text-slate-700 text-base md:text-lg leading-relaxed">
                  <strong className="text-slate-900 font-heading">{it.strong}</strong> {it.rest}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ===== FIDUCIA ===== */}
      <section id="fiducia" className="bg-white py-20 md:py-24 border-y border-slate-200">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-xs uppercase tracking-wider text-blue-600 font-semibold">Chi siamo</div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mt-2">Creato da chi conosce il lavoro in agenzia</h2>
          <p className="text-slate-700 mt-6 text-base md:text-lg leading-relaxed">
            {PRODUCT_NAME} nasce dall&apos;esperienza quotidiana nel settore assicurativo e dall&apos;esigenza di gestire incassi, versamenti e collaboratori in modo più semplice e ordinato.
          </p>
          <p className="text-slate-700 mt-4 text-base md:text-lg leading-relaxed">
            Non è un gestionale generico adattato alle assicurazioni, ma uno strumento progettato intorno alle esigenze reali delle agenzie.
          </p>
          {/* Placeholder testimonianza — attivabile in futuro quando disponibile.
              Attualmente nascosto: nessun contenuto placeholder visibile in landing.
              <TestimonialCard /> */}
        </div>
      </section>

      {/* ===== SICUREZZA E DATI ===== */}
      <section id="sicurezza" className="py-20 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-wider text-blue-600 font-semibold">Sicurezza e dati</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mt-2">Dove stanno i tuoi dati, e chi può vederli.</h2>
            <p className="text-slate-600 mt-4 text-base md:text-lg">
              Le domande che fa ogni agenzia prima di affidare a qualcuno i dati dei propri contraenti. Ecco le risposte, senza giri di parole.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {[
              { icon: ShieldCheck, title: "I dati restano in Italia", desc: "Il servizio è ospitato su infrastruttura Aruba, nel data center di Arezzo. I tuoi dati non lasciano il territorio italiano." },
              { icon: ShieldCheck, title: "Data center certificato", desc: "Il data center che ospita il servizio ha la certificazione Rating 4 ANSI/TIA-942, il livello più alto previsto dallo standard per la continuità operativa, ed è certificato ISO/IEC 27001 per la gestione della sicurezza delle informazioni." },
              { icon: Users, title: "Ogni agenzia vede solo i propri dati", desc: "Fogli cassa, versamenti e riepiloghi sono separati per agenzia. Nessuna agenzia può accedere ai dati di un'altra, e i collaboratori vedono soltanto la propria cassa." },
              { icon: Lock, title: "Password mai in chiaro", desc: "Le password non vengono mai conservate leggibili: il sistema ne memorizza solo un'impronta cifrata, diversa per ogni utente. Dopo alcuni tentativi di accesso falliti l'utenza viene bloccata temporaneamente." },
              { icon: Lock, title: "Nessun dato di pagamento sui nostri sistemi", desc: "Gli abbonamenti sono gestiti tramite Stripe. I dati della tua carta non transitano né vengono conservati sui sistemi di Incassi.cloud." },
              { icon: ShieldCheck, title: "Backup giornaliero", desc: "I dati vengono salvati ogni giorno su due destinazioni distinte, in modo da poter essere ripristinati in caso di guasto." },
            ].map((it) => (
              <Card key={it.title} className="p-6 border-slate-200 hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center mb-4">
                  <it.icon className="w-5 h-5 text-blue-600" aria-hidden="true" />
                </div>
                <h3 className="font-heading font-semibold text-slate-900">{it.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{it.desc}</p>
              </Card>
            ))}
          </div>

          <div className="mt-12 max-w-3xl">
            <div className="text-sm font-heading font-semibold text-slate-900">Trattamento dei dati</div>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              Incassi.cloud tratta i dati per conto dell&apos;agenzia, che ne resta titolare. All&apos;attivazione è disponibile l&apos;accordo sul trattamento dei dati personali previsto dall&apos;art. 28 del Regolamento (UE) 2016/679. Per richiederlo: <a href="mailto:info@incassi.cloud" className="text-blue-700 hover:text-blue-800 underline">info@incassi.cloud</a>
            </p>
          </div>
        </div>
      </section>

      {/* ===== PRICING ===== */}
      <section id="prezzi" className="bg-white py-20 md:py-24 border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4 md:px-8 text-center">
          <div className="text-xs uppercase tracking-wider text-blue-600 font-semibold">Piani</div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mt-2">Scegli il piano giusto.</h2>
          <p className="text-slate-600 mt-3 max-w-2xl mx-auto">Dal singolo collaboratore autonomo alle agenzie con team numerosi. {TRIAL_DAYS} giorni di prova gratuita su ogni piano, senza carta.</p>

          <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-5 max-w-3xl mx-auto text-left">
            <div className="font-heading font-semibold text-slate-900">Quante persone utilizzeranno {PRODUCT_NAME}?</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-sm">
              {plans.map((p) => (
                <div key={p.id} className={`rounded-md border px-3 py-2 ${p.highlight ? "border-blue-400 bg-blue-50 text-blue-800" : "border-slate-200 bg-white text-slate-700"}`}>
                  <div className="text-xs uppercase tracking-wider text-slate-500">{p.userLabel}</div>
                  <div className="font-semibold">{p.name}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="inline-flex bg-slate-100 rounded-full p-1 mt-8" data-testid="pricing-cycle-toggle" role="tablist" aria-label="Selettore periodo di fatturazione">
            <button
              type="button"
              onClick={() => setBillingCycle("month")}
              className={`px-4 py-1.5 text-sm font-semibold rounded-full transition-colors ${billingCycle === "month" ? "bg-white shadow text-slate-900" : "text-slate-600 hover:text-slate-900"}`}
              data-testid="pricing-cycle-month"
              role="tab"
              aria-selected={billingCycle === "month"}
            >Mensile</button>
            <button
              type="button"
              onClick={() => setBillingCycle("year")}
              className={`px-4 py-1.5 text-sm font-semibold rounded-full transition-colors inline-flex items-center gap-2 ${billingCycle === "year" ? "bg-white shadow text-slate-900" : "text-slate-600 hover:text-slate-900"}`}
              data-testid="pricing-cycle-year"
              role="tab"
              aria-selected={billingCycle === "year"}
            >
              Annuale
              <span className="text-[10px] uppercase font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">-5%</span>
            </button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto mt-10 px-4 md:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
          {plans.map((p) => (
            <Card key={p.id} className={`relative p-7 border-slate-200 card-elevated flex flex-col h-full ${p.highlight ? "ring-2 ring-blue-600 shadow-lg" : ""}`}>
              {p.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-wider text-white font-bold bg-blue-600 px-2.5 py-1 rounded-full shadow whitespace-nowrap">
                  Più scelto
                </div>
              )}
              <div className="text-xs uppercase tracking-wider text-blue-600 font-semibold">{p.name}</div>
              <div className="text-xs text-slate-500 mt-0.5 min-h-[1.25rem]">{p.tagline}</div>
              <div className="mt-4">
                <div className="flex items-baseline gap-1">
                  <span className="font-heading text-4xl font-bold text-slate-900">€ {billingCycle === "year" ? p.priceYear : p.price}</span>
                  <span className="text-slate-500 text-sm">/ {billingCycle === "year" ? "anno" : "mese"}</span>
                </div>
                <div className="text-xs text-emerald-700 font-medium mt-1 min-h-[1rem]">
                  {billingCycle === "year" ? "Risparmi il 5% rispetto al pagamento mensile" : "\u00A0"}
                </div>
              </div>
              <ul className="space-y-2.5 mt-5 flex-1">
                {p.feats.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" aria-hidden="true" /> {b}
                  </li>
                ))}
              </ul>
              <Link to={`/registrazione?plan=${p.id}&cycle=${billingCycle}`} className="block mt-6">
                <Button className={`w-full ${p.highlight ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-slate-900 hover:bg-slate-800 text-white"}`} size="lg" data-testid={`pricing-cta-${p.id}`}>
                  Prova gratis
                </Button>
              </Link>
            </Card>
          ))}
        </div>
        <p className="text-xs text-slate-500 text-center mt-6 max-w-2xl mx-auto px-4">
          Prezzo finale indicato prima del pagamento. Nessun costo nascosto. Puoi cambiare piano o disdire in qualsiasi momento dalla tua area Abbonamento.
        </p>
        <div className="mt-8 max-w-3xl mx-auto px-4" data-testid="custom-offer-note">
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-5 py-4 md:px-6 md:py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-slate-900">Serve un piano su misura?</div>
              <div className="text-sm text-slate-600 mt-0.5">
                Per un numero di utenze superiore o esigenze particolari creiamo un&apos;offerta personalizzata per la tua agenzia.
              </div>
            </div>
            <ScrollAnchorLink
              href="#contatti"
              className="shrink-0 inline-flex items-center justify-center rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium px-4 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              data-testid="custom-offer-contact-cta"
            >
              Contattaci
            </ScrollAnchorLink>
          </div>
        </div>
      </section>

      {/* ===== SERVIZI INCLUSI ===== */}
      <section id="servizi-inclusi" className="py-20 md:py-24 border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <div className="text-xs uppercase tracking-wider text-blue-600 font-semibold inline-flex items-center gap-1.5 justify-center">
              <Sparkles className="w-3.5 h-3.5" aria-hidden="true" /> Servizi inclusi
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mt-2">Servizi inclusi nei piani</h2>
            <p className="text-slate-600 mt-3">
              Le funzioni principali sono già comprese nel prezzo del tuo piano. Attivale o disattivale quando ti servono.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5 mt-10">
            {included.map((a) => (
              <Card key={a.name} className="p-6 border-slate-200 hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center mb-4">
                  <a.icon className="w-5 h-5 text-emerald-600" aria-hidden="true" />
                </div>
                <div className="flex items-baseline justify-between gap-2">
                  <h3 className="font-heading font-semibold text-slate-900">{a.name}</h3>
                </div>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{a.desc}</p>
                <div className="mt-3 text-[11px] text-emerald-700 font-semibold">✓ {a.tag}</div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section id="faq" className="bg-white py-20 md:py-24 border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="text-xs uppercase tracking-wider text-blue-600 font-semibold text-center">Domande frequenti</div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mt-2 text-center">FAQ</h2>
          <div className="mt-10 divide-y divide-slate-200 border-y border-slate-200" data-testid="faq-list">
            {faqs.map(([q, a], i) => {
              const open = openFaq === i;
              return (
                <div key={q} className="py-2">
                  <button
                    type="button"
                    className="w-full text-left flex items-center justify-between gap-4 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    aria-expanded={open}
                    aria-controls={`faq-panel-${i}`}
                    data-testid={`faq-toggle-${i}`}
                  >
                    <h3 className="font-heading font-semibold text-slate-900 text-base md:text-lg">{q}</h3>
                    <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform shrink-0 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
                  </button>
                  {open && (
                    <p id={`faq-panel-${i}`} className="text-sm text-slate-600 leading-relaxed pb-4 pr-8" data-testid={`faq-panel-${i}`}>{a}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== CONTATTI ===== */}
      <section id="contatti" className="bg-white py-20 md:py-24 border-t border-slate-100">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="text-center">
            <div className="text-xs uppercase tracking-wider text-blue-600 font-semibold">Contatti</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mt-2">Hai bisogno di vedere come funziona?</h2>
            <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
              Prenota una dimostrazione gratuita oppure scrivici. Ti aiutiamo a capire se {PRODUCT_NAME} è adatto alla tua agenzia.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-5 mt-10">
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="group p-6 rounded-2xl border border-slate-200 hover:border-blue-400 hover:shadow-md transition-all bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              data-testid="landing-email-link"
            >
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5 text-blue-700" aria-hidden="true" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Email</div>
                  <div className="font-heading font-semibold text-slate-900 mt-1 text-lg break-all">{SUPPORT_EMAIL}</div>
                  <div className="text-sm text-slate-600 mt-1">Risposta entro 24 ore lavorative</div>
                </div>
              </div>
            </a>
            <button
              type="button"
              onClick={openDemo}
              className="group text-left p-6 rounded-2xl border border-slate-200 hover:border-emerald-400 hover:shadow-md transition-all bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
              data-testid="landing-richiedi-call"
            >
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                  <Phone className="w-5 h-5 text-emerald-700" aria-hidden="true" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Demo gratuita</div>
                  <div className="font-heading font-semibold text-slate-900 mt-1 text-lg">Prenota una demo</div>
                  <div className="text-sm text-slate-600 mt-1">Configurazione assistita e dimostrazione del funzionamento</div>
                </div>
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* ===== CTA FINALE ===== */}
      <section className="bg-slate-900 text-white py-20 text-center">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <h2 className="font-heading text-3xl md:text-4xl font-bold">Porta ordine nella gestione degli incassi della tua agenzia.</h2>
          <p className="text-slate-300 mt-4 md:text-lg">
            Crea il tuo account in meno di un minuto e prova tutte le funzioni del piano scelto senza inserire la carta.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <Link to="/registrazione">
              <Button size="lg" className="bg-blue-500 hover:bg-blue-600 text-white" data-testid="footer-cta-primary">
                Prova gratis <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Button size="lg" variant="outline" type="button" onClick={openDemo} className="bg-white/5 border-white/20 text-white hover:bg-white/10" data-testid="footer-cta-demo">
              Prenota una demo
            </Button>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <PublicFooter />

      <RequestCallDialog
        open={callOpen}
        onOpenChange={setCallOpen}
        source="landing"
      />
    </div>
  );
}
